import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { UserX, TrendingDown, BarChart2, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => {
  if (!d) return '---';
  return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtMes = (m) => {
  const [ano, mes] = m.split('-');
  return `${MESES_ABREV[parseInt(mes, 10) - 1]}/${ano.slice(2)}`;
};

const corDias = (d) => d > 90 ? 'text-red-600' : d > 60 ? 'text-orange-500' : 'text-green-600';
const bgDias  = (d) => d > 90 ? 'bg-red-50 border-red-200' : d > 60 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200';

const BADGE_CFG = {
  critico: { cls: 'bg-red-100 text-red-700 border-red-300',         label: 'RISCO ALTO'  },
  alerta:  { cls: 'bg-orange-100 text-orange-700 border-orange-300', label: 'RISCO MÉDIO' },
  estavel: { cls: 'bg-green-100 text-green-700 border-green-300',   label: 'RISCO BAIXO' },
};

const ABC_CFG = {
  A: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', bar: '#10B981' },
  B: { cls: 'bg-amber-100 text-amber-700 border-amber-300',       bar: '#F59E0B' },
  C: { cls: 'bg-red-100 text-red-700 border-red-300',             bar: '#EF4444' },
};

const Spinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
  </div>
);

const ErroCarregar = () => (
  <div className="flex items-center justify-center py-12">
    <p className="text-sm text-red-500 font-semibold">Erro ao carregar dados</p>
  </div>
);

// ── SEÇÃO 0 — COMISSÃO PROJETADA ─────────────────────────────────────────────
const CARDS_CFG = [
  { key: 'comissao_recebida',    label: 'Recebida',      cor: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  { key: 'comissao_a_receber',   label: 'A Receber',     cor: 'text-[#0A3D73]',  bg: 'bg-blue-50',    border: 'border-blue-200',    dot: 'bg-[#0A3D73]'   },
  { key: 'comissao_prevista_30d',label: 'Próx. 30 dias', cor: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200',  dot: 'bg-orange-500'  },
  { key: 'comissao_atrasada',    label: 'Atrasada',      cor: 'text-red-600',    bg: 'bg-red-50',     border: 'border-red-200',     dot: 'bg-red-500'     },
];

function SecaoComissaoProjetada() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    api.get('/dashboard/comissao-projetada')
      .then(res => setDados(res.data))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, []);

  const chartData = (dados?.por_mes || []).map(m => ({
    mes: fmtMes(m.mes),
    valor: m.valor,
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
          <DollarSign size={16} className="text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-black text-slate-800">Comissão Projetada</p>
          <p className="text-xs text-slate-400">Análise de comissões por status temporal</p>
        </div>
      </div>

      {/* Body */}
      {loading ? <Spinner /> : erro ? <ErroCarregar /> : !dados ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm font-black text-slate-500">Sem dados de comissão</p>
          <p className="text-xs text-slate-400">Nenhum vencimento encontrado</p>
        </div>
      ) : (
        <div className="p-5">
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {CARDS_CFG.map(c => (
              <div key={c.key} className={`${c.bg} border ${c.border} rounded-xl p-4`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                  <p className="text-[10px] font-black uppercase tracking-wide text-slate-500">{c.label}</p>
                </div>
                <p className={`text-lg font-black ${c.cor} leading-none`}>{brl(dados[c.key] ?? 0)}</p>
              </div>
            ))}
          </div>

          {/* Gráfico por mês */}
          {chartData.length > 0 ? (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">
                A receber por mês de vencimento
              </p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [brl(v), 'Comissão']} labelStyle={{ fontWeight: 700 }} />
                  <Bar dataKey="valor" fill="#0A3D73" radius={[4, 4, 0, 0]} name="Comissão" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-[10px] text-slate-400 font-semibold text-center py-4">
              Sem vencimentos futuros pendentes
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── SEÇÃO 1 ──────────────────────────────────────────────────────────────────
function SecaoClientesSemCompra() {
  const [dias, setDias] = useState(30);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErro(false);
    api.get(`/dashboard/clientes-sem-compra?dias=${dias}`)
      .then(res => setClientes(res.data || []))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, [dias]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center">
            <UserX size={16} className="text-orange-500" />
          </div>
          <div>
            <p className="text-sm font-black text-slate-800">Clientes Sem Compra</p>
            <p className="text-xs text-slate-400">Sem pedido nos últimos {dias} dias</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDias(d)}
              className={`px-3 py-1 text-xs font-black rounded-lg border transition-all ${dias === d ? 'bg-[#0A3D73] text-white border-[#0A3D73]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#0A3D73] hover:text-[#0A3D73]'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : erro ? <ErroCarregar /> : clientes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm font-black text-green-600">Todos os clientes compraram recentemente</p>
          <p className="text-xs text-slate-400">Nenhum cliente sem pedido nos últimos {dias} dias</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Cliente</th>
                <th className="text-left px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Última Compra</th>
                <th className="text-center px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Dias sem Compra</th>
                <th className="text-right px-5 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Valor Médio</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c, i) => (
                <tr key={c.cliente_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-black text-slate-800 uppercase max-w-[200px] truncate">{c.cliente_nome || '---'}</td>
                  <td className="px-3 py-3 font-semibold text-slate-500 font-mono">{fmtDate(c.ultima_compra)}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-12 h-6 text-xs font-black rounded-full border ${bgDias(c.dias_sem_compra)} ${corDias(c.dias_sem_compra)}`}>
                      {c.dias_sem_compra}d
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-black text-slate-700">{brl(c.valor_medio_historico)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-2.5 text-[10px] text-slate-400 font-semibold border-t border-slate-100">
            {clientes.length} cliente{clientes.length > 1 ? 's' : ''} sem pedido nos últimos {dias} dias
          </p>
        </div>
      )}
    </div>
  );
}

// ── SEÇÃO 2 ──────────────────────────────────────────────────────────────────
function SecaoClientesEmRisco() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    api.get('/dashboard/clientes-em-risco')
      .then(res => setClientes(res.data || []))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
          <TrendingDown size={16} className="text-red-500" />
        </div>
        <div>
          <p className="text-sm font-black text-slate-800">Clientes em Risco</p>
          <p className="text-xs text-slate-400">Queda de faturamento &gt;25% vs. média 3 meses</p>
        </div>
      </div>

      {loading ? <Spinner /> : erro ? <ErroCarregar /> : clientes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm font-black text-green-600">Nenhum cliente em risco</p>
          <p className="text-xs text-slate-400">Todos os clientes mantêm faturamento estável</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Cliente</th>
                <th className="text-right px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Média 3 Meses</th>
                <th className="text-right px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Mês Atual</th>
                <th className="text-center px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Queda %</th>
                <th className="text-center px-5 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Tendência</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c, i) => {
                const badge = BADGE_CFG[c.tendencia] || BADGE_CFG.estavel;
                return (
                  <tr key={c.cliente_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 font-black text-slate-800 uppercase max-w-[180px] truncate">{c.cliente_nome || '---'}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-600">{brl(c.media_3_meses)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-600">{brl(c.valor_mes_atual)}</td>
                    <td className="px-3 py-3 text-center">
                      <span className="font-black text-red-600">{c.queda_percent?.toFixed(1)}%</span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-[9px] font-black px-2 py-1 rounded-full border ${badge.cls}`}>{badge.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-5 py-2.5 text-[10px] text-slate-400 font-semibold border-t border-slate-100">
            {clientes.length} cliente{clientes.length > 1 ? 's' : ''} com queda superior a 25%
          </p>
        </div>
      )}
    </div>
  );
}

// ── SEÇÃO 3 ──────────────────────────────────────────────────────────────────
function SecaoCurvaABC() {
  const ano = new Date().getFullYear();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    api.get(`/dashboard/curva-abc?ano=${ano}`)
      .then(res => setClientes(res.data || []))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, [ano]);

  const contA = clientes.filter(c => c.classificacao === 'A').length;
  const contB = clientes.filter(c => c.classificacao === 'B').length;
  const contC = clientes.filter(c => c.classificacao === 'C').length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
            <BarChart2 size={16} className="text-[#0A3D73]" />
          </div>
          <div>
            <p className="text-sm font-black text-slate-800">Curva ABC — Clientes</p>
            <p className="text-xs text-slate-400">Faturamento acumulado {ano}</p>
          </div>
        </div>
        {clientes.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg">{contA} A</span>
            <span className="font-black text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">{contB} B</span>
            <span className="font-black text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-lg">{contC} C</span>
          </div>
        )}
      </div>

      {loading ? <Spinner /> : erro ? <ErroCarregar /> : clientes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm font-black text-slate-500">Sem dados de faturamento</p>
          <p className="text-xs text-slate-400">Nenhuma NF emitida em {ano}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-center px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400 w-10">#</th>
                <th className="text-left px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Cliente</th>
                <th className="text-right px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Valor Total</th>
                <th className="text-right px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">%</th>
                <th className="text-left px-3 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400 w-36">% Acumulado</th>
                <th className="text-center px-5 py-3 text-[10px] font-black uppercase tracking-wide text-slate-400">Classe</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c, i) => {
                const cfg = ABC_CFG[c.classificacao] || ABC_CFG.C;
                return (
                  <tr key={c.cliente_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3 text-center text-slate-400 font-bold">{i + 1}</td>
                    <td className="px-3 py-3 font-black text-slate-800 uppercase max-w-[180px] truncate">{c.cliente_nome || '---'}</td>
                    <td className="px-3 py-3 text-right font-black text-slate-700">{brl(c.valor_total)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-500">{c.percentual?.toFixed(1)}%</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full"
                            style={{ width: `${Math.min(c.percentual_acumulado, 100)}%`, background: cfg.bar }} />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 w-9 text-right">{c.percentual_acumulado?.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-xs font-black px-2 py-1 rounded-full border ${cfg.cls}`}>{c.classificacao}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-5 py-2.5 text-[10px] text-slate-400 font-semibold border-t border-slate-100">
            {clientes.length} clientes · A = top 80% do faturamento · B = 80–95% · C = demais
          </p>
        </div>
      )}
    </div>
  );
}

// ── PÁGINA ────────────────────────────────────────────────────────────────────
const InteligenciaComercial = () => (
  <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen font-sans antialiased text-slate-800">
    <div className="mb-6">
      <h1 className="text-2xl font-black text-slate-900">Inteligência Comercial</h1>
      <p className="text-xs text-slate-500 font-semibold mt-0.5">comissão projetada, clientes em risco, clientes sem compra e curva ABC</p>
    </div>

    <div className="flex flex-col gap-6">
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Comissão Projetada</p>
        <SecaoComissaoProjetada />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Seção 1 — Clientes Sem Compra</p>
        <SecaoClientesSemCompra />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Seção 2 — Clientes em Risco</p>
        <SecaoClientesEmRisco />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Seção 3 — Curva ABC</p>
        <SecaoCurvaABC />
      </div>
    </div>
  </div>
);

export default InteligenciaComercial;
